import { useState } from "react";
import { useAuth } from "@/contexts/AuthContext";
import { api } from "@/lib/api";
import { CalendarHeart, AlertCircle } from "lucide-react";

/**
 * Blocks the app for paid users who haven't set their date of birth yet.
 * Retirement vault age-60 enforcement requires this. Cannot be skipped.
 */
export default function DobGate() {
  const { user, refresh } = useAuth();
  const [dob, setDob] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");

  if (!user || !user.paid) return null;
  if (user.date_of_birth) return null;

  const submit = async () => {
    if (!dob) { setError("Date of birth is required."); return; }
    setBusy(true); setError("");
    try {
      await api.post("/auth/dob", { date_of_birth: dob });
      if (refresh) await refresh();
    } catch (e) {
      setError(e.response?.data?.detail || e.message);
    } finally { setBusy(false); }
  };

  return (
    <div className="fixed inset-0 z-[90] flex items-center justify-center p-4 bg-ink/70 backdrop-blur-sm" data-testid="dob-gate-modal">
      <div className="bg-cream rounded-3xl border border-sand max-w-md w-full p-7 sm:p-9 shadow-2xl">
        <div className="w-12 h-12 rounded-2xl bg-forest/10 flex items-center justify-center">
          <CalendarHeart className="w-6 h-6 text-forest" strokeWidth={1.5} />
        </div>
        <h2 className="font-serif text-2xl sm:text-3xl tracking-tight mt-5">One last thing.</h2>
        <p className="text-sm text-moss mt-2 leading-relaxed">
          We need your date of birth to anchor your retirement vault unlock to your 60th birthday.
          This is set once and can&apos;t be changed.
        </p>
        <label className="block mt-6">
          <span className="text-[10px] uppercase tracking-[0.2em] font-semibold text-sage">Date of birth</span>
          <input type="date" value={dob} onChange={(e) => setDob(e.target.value)}
            max={new Date().toISOString().slice(0,10)}
            className="mt-2 w-full bg-white border border-sand rounded-lg px-4 py-3 text-sm"
            data-testid="dob-gate-input" />
        </label>
        {error && (
          <div className="mt-4 flex items-start gap-2 text-xs text-clay bg-clay-light px-3 py-2 rounded-lg" data-testid="dob-gate-error">
            <AlertCircle className="w-3.5 h-3.5 shrink-0 mt-0.5" />
            <span>{error}</span>
          </div>
        )}
        <button onClick={submit} disabled={busy || !dob}
          className="mt-6 w-full bg-forest text-white rounded-xl py-3 text-sm font-medium hover:bg-forest-deep disabled:opacity-60"
          data-testid="dob-gate-submit-btn">
          {busy ? "Saving…" : "Save & continue"}
        </button>
      </div>
    </div>
  );
}
